import {useMemo} from 'react'
import {useProduct, useProducts} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {DEFAULT_GET_PRODUCT_PRICE_PARAMETERS} from './useProductDetail'
import {getProductsToAddToCart, isProductOrderable, isProductSet} from '../utils/product-utils'
import {useProductSetFullPrices, useProductSetPrice} from './useScapiCustomApi'
import {MAX_IDS_PER_GET_PRODUCTS_REQUEST} from '../constants'

/**
 * Retrieve product name given productId
 * @param {string} productId - Product id to retrieve the name of
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query
 * @returns {import("@tanstack/react-query").UseQueryResult<{name: string}>} Query result where data contains an object with a single property `name` of type string
 */
export const useProductName = (productId, queryOptions) =>
    useProduct(
        {
            parameters: {
                id: productId,
                select: '(name)'
            }
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            enabled: Boolean(productId) && queryOptions?.enabled !== false
        }
    )

/**
 * Fetch specific product price based on currently selected set products and variants.
 *
 * @param {object} product - Product to retrieve pricing data for - must have the isSetProductSelected attributes
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query
 * @returns {import("@tanstack/react-query").UseQueryResult<{
 *     c_taxRate: number,
 *     productPromotions: Array,
 *     price: number,
 *     setAccessories: Array<{
 *       id: string,
 *       c_taxRate: number,
 *       productPromotions: Array,
 *       price: number
 *     }>,
 *     setExtensions: Array<{
 *       id: string,
 *       c_taxRate: number,
 *       productPromotions: Array,
 *       price: number
 *     }>,
 *     setProducts: Array<{
 *       id: string,
 *       c_taxRate: number,
 *       productPromotions: Array,
 *       price: number
 *     }>,
 *   }>} Query result where data contains the pricing information for the product
 */
export const useProductPrice = (product, queryOptions) => {
    const productIsSet = useMemo(() => isProductSet(product), [product])

    const setProducts = useMemo(
        () =>
            productIsSet
                ? [
                      ...(product?.setProducts || []),
                      ...(product?.setExtensions || []),
                      ...(product?.setAccessories || [])
                  ].filter(Boolean)
                : [],
        [product, productIsSet]
    )

    const selectedSetProducts = useMemo(
        () =>
            productIsSet
                ? [
                      ...getProductsToAddToCart(product),
                      // engraving is added automatically by sfcc when engaving custom attribute is added when adding engravable product to cart
                      // so we need to manually simulate that here
                      ...(product?.engraving?.isSetProductSelected ? [product?.engraving] : [])
                  ]
                : [],
        [product, productIsSet]
    )
    const unselectedSetProducts = useMemo(() => {
        const selectedSet = new Set(selectedSetProducts.map((p) => p.id))
        return setProducts.filter((p) => !selectedSet.has(p.id))
    }, [setProducts, selectedSetProducts])

    const priceParameters = useMemo(() => {
        const c_selected = selectedSetProducts
            .map((p) => p.id)
            .sort((a, b) => a.localeCompare(b))
            .join('|')
        if (!c_selected.length) return
        const c_unselected = unselectedSetProducts
            .map((p) => p.id)
            .sort((a, b) => a.localeCompare(b))
            .join('|')
        return {
            c_selected,
            ...(c_unselected.length > 0 && {c_unselected})
        }
    }, [selectedSetProducts, unselectedSetProducts])

    const productPriceQueryResult = useProductPreliminaryPrice(product, {enabled: !productIsSet})

    const setProductPriceQueryResult = useProductSetFullPrices(
        {
            parameters: priceParameters
        },
        {
            ...getConfig().app.queryCache?.availabilityPrice,
            keepPreviousData: false,
            ...queryOptions,
            enabled: Boolean(productIsSet && priceParameters) && queryOptions?.enabled !== false
        }
    )

    const setProductPriceData = useMemo(() => {
        // convert the set product's price and promotions
        const convert = (setProduct) => {
            // use the correct list
            const list = selectedSetProducts.some((p) => p.id === setProduct.id)
                ? setProductPriceQueryResult.data?.selected?.productLineItems
                : setProductPriceQueryResult.data?.unselected
            // find the product line for the set product - look for the variant id
            const productLine = list?.find((p) => p.variantId === setProduct.id)

            if (!productLine) {
                return null
            }

            // convert the set product price
            return {
                c_hidePromotionSignals:
                    productLine.c_hidePromotionSignals ?? setProduct.c_hidePromotionSignals,
                c_taxRate:
                    productLine.c_taxRate ??
                    setProductPriceQueryResult.data?.selected?.c_taxRate ??
                    setProduct.c_taxRate,
                id: setProduct.id,
                price: productLine.price,
                productPromotions: [
                    // add promotional price for the set product
                    ...(productLine?.adjustedPrice != null
                        ? [
                              {
                                  promotionalPrice: productLine?.adjustedPrice
                              }
                          ]
                        : []),
                    // convert the regular promotions
                    ...(productLine.pliPromotions || productLine.promotions || []).map(
                        (promotionId) => ({
                            promotionId
                        })
                    )
                ]
            }
        }

        // convert the price and promotions of the product set
        return {
            c_hidePromotionSignals:
                setProductPriceQueryResult.data?.selected?.c_hidePromotionSignals ??
                product?.c_hidePromotionSignals,
            c_taxRate: setProductPriceQueryResult.data?.selected?.c_taxRate ?? product?.c_taxRate,
            price: setProductPriceQueryResult.data?.selected?.price ?? product?.price,
            productPromotions:
                setProductPriceQueryResult.data?.selected?.productPromotions ??
                product?.productPromotions,
            setProducts: product?.setProducts?.map(convert).filter(Boolean),
            setExtensions: product?.setExtensions?.map(convert).filter(Boolean),
            setAccessories: product?.setAccessories?.map(convert).filter(Boolean),
            engraving: product?.engraving && convert(product?.engraving)
        }
    }, [product, setProductPriceQueryResult.data, selectedSetProducts])

    if (productIsSet) {
        return {
            ...setProductPriceQueryResult,
            data: setProductPriceData
        }
    }

    return productPriceQueryResult
}

/**
 * Fetch product price before configuring any variant or set products
 * @param {Object} params - Parameters object
 * @param {string} params.id - Product id to retrieve pricing data for
 * @param {Object} [params.type] - SFCC product type structure (usually has a single property with value true). When not provided, loads for both product set and non-set, which is slower.
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions] - Options to configure the behavior of the query
 * @returns {{
 *   data: {
 *     c_taxRate: number,
 *     productPromotions: Array,
 *     price: number
 *   },
 *   error: Error,
 *   isError: boolean,
 *   isFetching: boolean,
 *   isLoading: boolean,
 *   isInitialLoading: boolean
 * }}
 */
export const useProductPreliminaryPrice = ({id, type}, queryOptions) => {
    // load price data for product
    const productQuery = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PRICE_PARAMETERS,
                id
            }
        },
        {
            ...getConfig().app.queryCache?.availabilityPrice,
            ...queryOptions,
            // fetch only for product
            enabled:
                Boolean(id && (!type || !isProductSet({type}))) && queryOptions?.enabled !== false
        }
    )
    // load price data for product set
    const productSetQuery = useProductSetPrice(
        {
            parameters: {
                c_pid: id
            }
        },
        {
            ...getConfig().app.queryCache?.availabilityPrice,
            ...queryOptions,
            // fetch only for product set
            enabled:
                Boolean(id && (!type || isProductSet({type}))) && queryOptions?.enabled !== false
        }
    )

    const productData = useMemo(
        () => ({
            c_hidePromotionSignals:
                productSetQuery.data?.resp?.c_hidePromotionSignals ??
                productQuery.data?.c_hidePromotionSignals,
            c_taxRate: productSetQuery.data?.resp?.c_taxRate ?? productQuery.data?.c_taxRate,
            productPromotions:
                productSetQuery.data?.resp?.productPromotions ??
                productQuery.data?.productPromotions,
            price: productSetQuery.data?.resp?.price ?? productQuery.data?.price
        }),
        [productQuery.data, productSetQuery.data]
    )

    return {
        ...(!type
            ? {
                  error: productQuery.error || productSetQuery.error,
                  isError: productQuery.isError || productSetQuery.isError,
                  isFetching: productQuery.isFetching || productSetQuery.isFetching,
                  isInitialLoading:
                      productQuery.isInitialLoading || productSetQuery.isInitialLoading,
                  isLoading: productQuery.isLoading || productSetQuery.isLoading
              }
            : isProductSet({type})
            ? {
                  error: productSetQuery.error,
                  isError: productSetQuery.isError,
                  isFetching: productSetQuery.isFetching,
                  isInitialLoading: productSetQuery.isInitialLoading,
                  isLoading: productSetQuery.isLoading
              }
            : {
                  error: productQuery.error,
                  isError: productQuery.isError,
                  isFetching: productQuery.isFetching,
                  isInitialLoading: productQuery.isInitialLoading,
                  isLoading: productQuery.isLoading
              }),
        data: productData
    }
}

/**
 * Fetch orderable status for a list of variant product IDs.
 * Returns a flat map of variantId → orderable boolean, derived from `c_availability`
 * (instock/lowstock = true) with `orderable` as fallback.
 *
 * Fires up to 4 parallel requests to stay within MAX_IDS_PER_GET_PRODUCTS_REQUEST.
 *
 * @param {string[]} variantIds - Variant product IDs to fetch availability for
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult<Record<string, boolean>>} map from variant id to orderable flag
 */
export const useProductVariantsOrderable = (variantIds, queryOptions) => {
    const cacheOptions = getConfig().app.queryCache?.availabilityPrice
    const baseEnabled = variantIds?.length > 0 && queryOptions?.enabled !== false
    const commonParameters = {
        select: '(data.(id,c_isOrderable))'
    }
    const chunks = useMemo(
        () =>
            Array.from(
                {length: Math.ceil(variantIds?.length / MAX_IDS_PER_GET_PRODUCTS_REQUEST)},
                (_, i) =>
                    variantIds.slice(
                        i * MAX_IDS_PER_GET_PRODUCTS_REQUEST,
                        (i + 1) * MAX_IDS_PER_GET_PRODUCTS_REQUEST
                    )
            ),
        [variantIds]
    )

    // Hooks rules prevent dynamic call counts, so we use up to 4 fixed calls with conditional
    // `enabled` to cover all IDs in parallel without exceeding the per-request limit.
    const chunk1 = useProducts(
        {parameters: {...commonParameters, ids: chunks[0]}},
        {...cacheOptions, ...queryOptions, enabled: baseEnabled && chunks[0] != null}
    )
    const chunk2 = useProducts(
        {parameters: {...commonParameters, ids: chunks[1]}},
        {...cacheOptions, ...queryOptions, enabled: baseEnabled && chunks[1] != null}
    )
    const chunk3 = useProducts(
        {parameters: {...commonParameters, ids: chunks[2]}},
        {...cacheOptions, ...queryOptions, enabled: baseEnabled && chunks[2] != null}
    )
    const chunk4 = useProducts(
        {parameters: {...commonParameters, ids: chunks[3]}},
        {...cacheOptions, ...queryOptions, enabled: baseEnabled && chunks[3] != null}
    )

    const orderableByProductId = useMemo(
        () =>
            Object.fromEntries(
                [
                    ...(chunk1.data?.data ?? []),
                    ...(chunk2.data?.data ?? []),
                    ...(chunk3.data?.data ?? []),
                    ...(chunk4.data?.data ?? [])
                ]
                    .filter(Boolean)
                    .map((product) => [product.id, isProductOrderable(product)])
            ),
        [chunk1.data, chunk2.data, chunk3.data, chunk4.data]
    )

    return {
        ...chunk1,
        isFetching:
            chunk1.isFetching || chunk2.isFetching || chunk3.isFetching || chunk4.isFetching,
        isLoading:
            chunk1.isLoading ||
            (chunks[1] != null && chunk2.isLoading) ||
            (chunks[2] != null && chunk3.isLoading) ||
            (chunks[3] != null && chunk4.isLoading),
        isError: chunk1.isError || chunk2.isError || chunk3.isError || chunk4.isError,
        error: chunk1.error ?? chunk2.error ?? chunk3.error ?? chunk4.error,
        data: orderableByProductId
    }
}
